import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

interface TableFormValues {
  id?: string;
  tableLabel: string;
  seatCapacity: number;
  isQRMenuOrderEnabled: boolean;
}

interface TableFormModalProps {
  isOpen: boolean;
  initial?: TableFormValues | null;
  onClose: () => void;
  onSave: (vals: TableFormValues) => Promise<boolean | void>;
}

const TableFormModal: React.FC<TableFormModalProps> = ({ isOpen, initial = null, onClose, onSave }) => {
  const [tableLabel, setTableLabel] = useState("");
  const [seatCapacity, setSeatCapacity] = useState<number | "">("");
  const [isQRMenuOrderEnabled, setIsQRMenuOrderEnabled] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTableLabel(initial?.tableLabel || "");
    setSeatCapacity(initial?.seatCapacity ?? "");
    // When creating a new table (initial is null) default isQRMenuOrderEnabled to true
    setIsQRMenuOrderEnabled(initial?.isQRMenuOrderEnabled ?? true);
  }, [isOpen, initial]);

  const doSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!tableLabel.trim()) return;
    const capacity = typeof seatCapacity === "string" ? parseInt(seatCapacity || "0", 10) : seatCapacity;
    if (!capacity || capacity <= 0) return;
    setSubmitting(true);
    try {
      const payload: TableFormValues = {
        id: initial?.id,
        tableLabel: tableLabel.trim(),
        seatCapacity: capacity,
        isQRMenuOrderEnabled
      };
      const res = await onSave(payload);
      // onSave may return boolean to indicate success
      if (res === false) {
        // keep modal open
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="tm-modal-backdrop" onClick={onClose}>
      <div className="tm-modal tm-modal-responsive bg-white dark:bg-[#23160d]" onClick={(e) => e.stopPropagation()}>
        <header className="tm-modal-head border-b border-[#ffe0b2] dark:border-[#3a2412]">
          <h4>{initial ? "Edit Table" : "Add Table"}</h4>
          <button className="icon-btn text-[#ff512f] dark:text-[#ffb347]" onClick={onClose}><X /></button>
        </header>

        <div className="tm-modal-body">
          <form onSubmit={doSave} className="w-full max-w-md">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Table Label</label>
            <input value={tableLabel} onChange={(e) => setTableLabel(e.target.value)} placeholder="e.g., Table 5, Room 201, Patio 2, VIP A, Sapphire Lounge 1" className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#23272f] text-gray-900 dark:text-gray-100 text-sm md:text-base placeholder:text-xs px-2 py-2 md:px-3 md:py-2 mt-1 mb-3" required />

            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Seating Capacity</label>
                <input
                  value={seatCapacity}
                  onChange={(e) => setSeatCapacity(e.target.value === "" ? "" : Number(e.target.value))}
                  type="number"
                  min={1}
                  placeholder="e.g. 4"
                  onWheel={(e) => e.preventDefault()}
                  className="w-full rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-[#23272f] text-gray-900 dark:text-gray-100 text-sm md:text-base px-2 py-2 md:px-3 md:py-2 mt-1 mb-3"
                  required
                />

            <div className="flex items-center gap-3 mb-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isQRMenuOrderEnabled}
                  aria-label="Enable Order Feature"
                  onClick={() => setIsQRMenuOrderEnabled((s) => !s)}
                  className={`toggle-switch ${isQRMenuOrderEnabled ? 'on' : ''}`}
                >
                  <span className="toggle-thumb" />
                </button>
                <span className="text-sm text-gray-700 dark:text-gray-200">Enable Order Feature</span>
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="tm-btn tm-btn-ghost bg-[#f3f4f6] text-[#111827] dark:bg-[#2d1c0e] dark:text-[#ffb347] text-sm md:text-base px-2 py-1 md:px-3 md:py-2">Cancel</button>
              <button type="submit" disabled={submitting} className="tm-btn tm-btn-primary bg-gradient-to-r from-[#ff512f] to-[#ff9800] text-white text-sm md:text-base px-2 py-1 md:px-3 md:py-2">{initial ? "Update" : "Add"}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TableFormModal;
